import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../../staff/StaffSideMenu";
import API_URL from "../../../api/API";
import { useStaffAuthContext } from "../../staff/useStaffAuthContext";
import { Link, useParams, useNavigate } from 'react-router-dom';

export const ExerciseManage = () => {
  const { staff } = useStaffAuthContext();
  const { phase_id } = useParams(); // Get the :id from the URL
  const navigate = useNavigate(); // For the "Go Back" button

  const [exercises, setExercises] = useState([]);
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchExercises = async (page) => {
    try {
      setIsLoading(true);
      setError("");
      const response = await fetch(
        `${API_URL.GET_PHASE_EXERCISE_ALL}${phase_id}?page=${page}&limit=50`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        }
      );
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to fetch phases");
      }

      setExercises(data.phaseExercises);
      setFilteredExercises(data.phaseExercises);
      setCurrentPage(data.currentPage);
      setTotalPages(data.totalPages);
      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  const deleteExercise = async (id) => {
    try {
      setIsDeleting(true);
      const response = await fetch(`${API_URL.DELETE_PHASE_EXERCISE}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${staff.token}`,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to delete exercise");
      }

      // Refresh the phases list
      fetchExercises(currentPage);
      alert("Exercise deleted successfully!");
    } catch (err) {
      alert(`Error deleting exercise: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this exercise?")) {
      deleteExercise(id);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    filterExercises(query);
  };

  const filterExercises = (query) => {
    if (query.trim() === "") {
      setFilteredExercises(exercises);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = exercises.filter((exercise) =>
      exercise.title.toLowerCase().includes(lowerQuery)
    );

    setFilteredExercises(filtered);
  };

  useEffect(() => {
    fetchExercises(currentPage);
  }, [currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />

      <div className="flex-1 p-6 bg-gray-100 pt-16 flex flex-col">

        {/* Go Back Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => navigate(-1)} // Navigate back to the previous page
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Go Back
          </button>
        </div>

        <h1 className="text-2xl font-bold mb-4">Manage Exercises Inside Phase</h1>

        {/* Search Input */}
        <div className="flex justify-end gap-4 mb-4">
          <input
            type="text"
            placeholder="Search by title"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-1/4 p-2 border border-gray-300 rounded"
          />
        </div>

        {/* Phases Table */}
        <div className="flex-1 overflow-auto border rounded shadow bg-white">
          <table className="table-auto w-full">
            <thead className="sticky top-0 bg-gray-200">
              <tr>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="2" className="text-center py-4">
                    <p className="text-blue-600">Loading data...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="2" className="text-center py-4">
                    <p className="text-red-600">{error}</p>
                  </td>
                </tr>
              ) : filteredExercises.length === 0 ? (
                <tr>
                  <td colSpan="2" className="text-center py-4">
                    <p>No records found</p>
                  </td>
                </tr>
              ) : (
                filteredExercises.map((exercise, index) => (
                  <tr
                    key={exercise._id}
                    className={`hover:bg-gray-100 border-b border-gray-200 ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <td className="px-4 py-2">{exercise.title}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleDelete(exercise._id)}
                          className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                          disabled={isDeleting}
                        >
                          Delete
                        </button>
                      {/*  <Link
                          to={`/administrator/warmup-routine/edit-phase-exercise/${exercise.phase_id}/${exercise._id}`}
                          className="px-3 py-1 text-xs bg-blue-500 text-white text-center rounded hover:bg-blue-600"
                        >
                          Edit Exercise
                        </Link> */}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded ${
              currentPage === 1
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Previous
          </button>

          <p className="text-sm">
            Page {currentPage} of {totalPages}
          </p>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded ${
              currentPage === totalPages
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
